import React from "react";
import "./PronoiaPatientIntelligence.css";
import PageHeader from "../../../components/headers/PageHeader";
import wave from "../../../assets/Images/waves/mix_wave.jpg";

export default function PronoiaPatientIntelligence() {
    return (
        <div className="ppi-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Patient Feasibility Analytics"
                title="Pronoia Patient Intelligence™"
            />

            <section className="ppi-section ppi-introSection">
                <h2>Patient cohort intelligence for smarter clinical development planning</h2>

                <p>
                    Pronoia Patient Intelligence™ is a patient cohort intelligence
                    and population feasibility analytics platform designed to help
                    sponsors and clinical development teams understand where the
                    right patients are, which regions and sites may provide access
                    to those patients, and how protocol criteria may affect study
                    feasibility.
                </p>
            </section>

            <section className="ppi-section">
                <h2>What the Platform Helps Answer</h2>

                <p className="ppi-sectionText">
                    Early clinical development decisions depend on understanding
                    patient availability, site access, regional distribution, and
                    protocol feasibility. Pronoia Patient Intelligence™ helps teams
                    explore these questions using available datasets and structured
                    cohort analytics.
                </p>

                <ul className="ppi-list">
                    <li>Where are the right patients located?</li>
                    <li>Which sites may have access to eligible patients?</li>
                    <li>What does the target patient population look like?</li>
                    <li>Which inclusion or exclusion criteria may be too restrictive?</li>
                    <li>Which regions should be prioritized for feasibility planning?</li>
                    <li>How many patients remain after applying protocol criteria?</li>
                </ul>
            </section>

            <section className="ppi-section">
                <h2>Supported Data Sources</h2>

                <p className="ppi-sectionText">
                    The platform can work with structured and semi-structured data
                    provided by sponsors, CROs, vendors, research networks, or
                    clinical data partners.
                </p>

                <div className="ppi-twoColumn">
                    <div>
                        <h3>Patient and Clinical Data</h3>
                        <ul className="ppi-list">
                            <li>De-identified patient records</li>
                            <li>Claims extracts</li>
                            <li>Diagnosis codes</li>
                            <li>Medication history</li>
                            <li>Laboratory values</li>
                        </ul>
                    </div>

                    <div>
                        <h3>Operational and Site Data</h3>
                        <ul className="ppi-list">
                            <li>Site enrollment history</li>
                            <li>CRO or vendor CSV exports</li>
                            <li>Excel feasibility trackers</li>
                            <li>Regional patient availability datasets</li>
                            <li>Historical study performance data</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="ppi-section">
                <h2>Key Capabilities</h2>

                <div className="ppi-twoColumn">
                    <div>
                        <h3>Cohort Counts</h3>
                        <p>
                            Estimate patient population size across defined
                            diagnosis, treatment, demographic, laboratory, and
                            eligibility-related parameters.
                        </p>
                    </div>

                    <div>
                        <h3>Population Breakdowns</h3>
                        <p>
                            Review age, sex, race, region, diagnosis, comorbidity,
                            treatment history, and other available population
                            characteristics.
                        </p>
                    </div>

                    <div>
                        <h3>Criteria Impact Simulation</h3>
                        <p>
                            Simulate how inclusion and exclusion criteria reduce or
                            reshape the available patient population during protocol
                            planning.
                        </p>
                    </div>

                    <div>
                        <h3>Site and Region Intelligence</h3>
                        <p>
                            Identify patient density patterns and support region or
                            site selection strategy using available site and cohort
                            data.
                        </p>
                    </div>
                </div>
            </section>

            <section className="ppi-section">
                <h2>Use Cases</h2>

                <ul className="ppi-list">
                    <li>Protocol feasibility assessment before study start-up</li>
                    <li>Country, region, and site selection support</li>
                    <li>Eligibility criteria optimization</li>
                    <li>Patient availability estimation for rare or targeted populations</li>
                    <li>Enrollment planning and recruitment risk review</li>
                    <li>Comparison of competing protocol design scenarios</li>
                    <li>Support for real-world data and feasibility analytics workflows</li>
                </ul>
            </section>

            <section className="ppi-section">
                <h2>Why Pronoia Patient Intelligence™</h2>

                <p className="ppi-sectionText">
                    Small and mid-sized sponsors often need stronger patient
                    feasibility intelligence before committing to expensive trial
                    execution decisions. Pronoia Patient Intelligence™ helps turn
                    available patient and operational datasets into practical
                    insights for study design, feasibility, and site planning.
                </p>

                <ul className="ppi-list">
                    <li>Supports earlier feasibility decisions with data-backed insight</li>
                    <li>Helps identify overly restrictive eligibility criteria</li>
                    <li>Improves regional and site selection confidence</li>
                    <li>Connects patient population analytics with operational planning</li>
                    <li>Helps teams move from assumptions to evidence-driven feasibility</li>
                </ul>
            </section>
        </div>
    );
}